import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { MCard, MGrid, MSpinner, MSplitter, MDialog, ActionButtons } from "components/Shared";
import { ACTION_ADD } from "components/Shared/MGrid.tsx";
import { EMPLOYEE_COURSE_EDIT, EMPLOYEE_COURSE_DELETE, EMPLOYEE_COURSE_ADD } from "typeConstants/rights";
import { userHasRight } from "selectors/rights";
import { getGroups, updateGroup, deleteGroup, addGroup } from "requests/courseGroup";
import Grid from "@mui/material/Grid";
import { DATA_TYPE_BOOLEAN, DATA_TYPE_DROPDOWN2, DATA_TYPE_NUMBER } from "typeConstants/dataTypes";
import { updateHeader, clearHeader } from "actions/layout";

// used an object for this instead of an array because i don't like the idea that in the array it isnt obvious that the order matters
export const CourseGroupEmailTemplates = {
  [1]: "Employment Requirement",
  [2]: "Training",
  [3]: "Training Requirement",
};

const ManageCourseGroups = ({
    canAddEmployeeCourses,
    canEditEmployeeCourses,
    canDeleteEmployeeCourses,
    history,
    updateHeader,
    clearHeader,
  }) => {
  const [groups, setGroups] = useState();
  const [selectedGridIndex, setSelectedGridIndex] = useState();
  const [selectedGroup, setSelectedGroup] = useState();
  const [isCreatingGroup, setIsCreatingGroup] = useState();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isSavingGroup, setIsSavingGroup] = useState(false);

  const mounted = useRef(true);

  const getGroupList = () => {
    getGroups(groups => {
      setGroups(groups);
      if (selectedGridIndex !== null) setSelectedGroup(groups[selectedGridIndex]); 
    }, mounted);
  };

  useEffect(() => {
    getGroupList();
    updateHeader({
      buttons: [{
        name: "Back To Manage Courses",
        onClick: () => history.push("/manage-courses"),
      }]
    });
    return () => {
      mounted.current = false;
      clearHeader();
    };
  }, []);
  
  const onGroupSelected = index => {
    setSelectedGridIndex(index);
    setSelectedGroup(groups[index]);
    setIsCreatingGroup(false);
  };

  const onCreateGroup = () => {
    setSelectedGridIndex(null);
    setSelectedGroup({
      id: null,
      name: "",
      url: "",
      daysTillDue: null,
      notificationEnabled: true,
      emailTemplateType: 1,
    });
    setIsCreatingGroup(true);
  };

  const onDeleteClicked = force => {
    if (force) {
      deleteGroup(selectedGroup.id, () => {
        setGroups(null);
        setSelectedGridIndex(null);
        setSelectedGroup(null);
        getGroupList();
      }, null, mounted);
      setShowDeleteDialog(false);
    } else {
      setShowDeleteDialog(true);
    }
  };

  const onEditCancelled = () => {
    if (isCreatingGroup) {
      setSelectedGroup(null);
      setIsCreatingGroup(false);
      setSelectedGridIndex(null);
    }
  };

  const onEditFinished = (_, editedGroup) => {
    if (isSavingGroup) return false;

    const getField = field => editedGroup[field] ?? selectedGroup[field];
    
    let newGroup = {
      id: selectedGroup.id,
      name: getField("name"),
      url: getField("url"),
      daysTillDue: getField("daysTillDue"),
      notificationEnabled: getField("notificationEnabled"),
      emailTemplateType: getField("emailTemplateType"),
    };

    setIsSavingGroup(true);

    const onGroupSaved = () => {
      setGroups(null);
      getGroupList();
      setIsCreatingGroup(false);
      setIsSavingGroup(false);
      setSelectedGroup(null);
    };

    if (isCreatingGroup) {
      addGroup(newGroup, onGroupSaved, () => setIsSavingGroup(false), mounted);
    } else {
      updateGroup(newGroup, onGroupSaved, () => setIsSavingGroup(false), mounted);
    }

    return false; // don't make the card quit edit mode in case the save errors
  };

  let grid = ( <MGrid
    headerText="Course Categories"
    actions={[
      canAddEmployeeCourses && { code: ACTION_ADD, onClick: onCreateGroup },
      canDeleteEmployeeCourses && selectedGroup && { icon: "delete", onClick: () => onDeleteClicked(false) },
    ].filter(a => a)}
    data={groups}
    onRowClick={i => onGroupSelected(i)}
    rowLastClicked={selectedGridIndex}
    columnDefinitions={[
      { name: "Name", width: 180, dataSource: "name" },
      { name: "Days Till Due", width: 180, dataSource: "daysTillDue", dataType: DATA_TYPE_NUMBER },
      { name: "URL", dataSource: "url" }, 
      { name: "Notification Enabled", width: 150, dataSource: "notificationEnabled", dataType: DATA_TYPE_BOOLEAN },
      { name: "Email Template", width: 130, dataSource: row => CourseGroupEmailTemplates[row.emailTemplateType] },
    ].map(d => ({ key: d.dataSource, ...d }))}
  />);

  let secondPane;

  if (selectedGroup) {
    let cardFields = [
      { key: "name", label: "Name", value: selectedGroup.name },
      { key: "daysTillDue", label: "Days Till Due", value: selectedGroup.daysTillDue, dataType: DATA_TYPE_NUMBER },
      { key: "url", label: "URL", value: selectedGroup.url }, 
      { key: "notificationEnabled", label: "Notification Enabled", value: selectedGroup.notificationEnabled, dataType: DATA_TYPE_BOOLEAN },
      {
        key: "emailTemplateType",
        label: "Email Template",
        value: { label: CourseGroupEmailTemplates[selectedGroup.emailTemplateType], value: selectedGroup.emailTemplateType },
        dataType: DATA_TYPE_DROPDOWN2,
        dataTypeOptions: {
          nullable: false,
          options: Object.keys(CourseGroupEmailTemplates)
                          .map(k => ({ label: CourseGroupEmailTemplates[k], value: parseInt(k) }))
                          .sort((a, b) => a.label.localeCompare(b.label)),
        },
      },
    ];
    
    // change the key to the card if we're making a new course so the startInEditMode prop works
    // also change the key per group to prevent the error of pressing edit and then selecting a new row in the grid
    secondPane = (<>
      <Grid container spacing={2}>
        <Grid size={12}>
          <MCard
            key={isCreatingGroup ? "new" : `current-${selectedGroup.id}`}
            title="Category Details"
            isEditable={canEditEmployeeCourses}
            startInEditMode={isCreatingGroup}
            onEditFinished={onEditFinished}
            onEditCancelled={onEditCancelled}
            fields={cardFields}
          />
        </Grid>
      </Grid>
    </>);
  }

  return groups ? <>
    <MSplitter
      firstChild={grid}
      secondChildTitle={{
        title: isCreatingGroup ? "New Category" : "Category Details",
        buttons: [{ icon: "close", onClick: () => { setSelectedGroup(null); setSelectedGridIndex(null); } }]
      }}
      secondChild={secondPane}
    />
    <MDialog
      visible={showDeleteDialog}
      width={350}
    >
      Are you sure you want to delete this category?
      <ActionButtons buttonPropSets={[
        { children: "Yes", onClick: () => onDeleteClicked(true) },
        { children: "No", onClick: () => setShowDeleteDialog(false) },
      ]} />
    </MDialog>
    </> :
    <MSpinner />;
};

ManageCourseGroups.propTypes = {
  history: PropTypes.object,
  updateHeader: PropTypes.func,
  clearHeader: PropTypes.func,
  makeErrorToast: PropTypes.func.isRequired,
  canAddEmployeeCourses: PropTypes.bool.isRequired,
  canEditEmployeeCourses: PropTypes.bool.isRequired,
  canDeleteEmployeeCourses: PropTypes.bool.isRequired,
};

const mapStateToProps = state => ({
  canAddEmployeeCourses: userHasRight(EMPLOYEE_COURSE_ADD)(state),
  canEditEmployeeCourses: userHasRight(EMPLOYEE_COURSE_EDIT)(state),
  canDeleteEmployeeCourses: userHasRight(EMPLOYEE_COURSE_DELETE)(state),
});

const mapDispatchToProps = dispatch => ({
  updateHeader(headerDefinitions) {
    dispatch(updateHeader(headerDefinitions));
  },
  clearHeader() {
    dispatch(clearHeader());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ManageCourseGroups);
